// src/com/app_main/AppMain.js
import React, { useEffect, useRef, useState } from 'react';
import { styled  } from '@mui/material/styles';
import { Box } from '@mui/system';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { clearReposForNewSession, mapRequestRepo, userInfoRepo } from '../../value/AppModel';
import { LOGOUT_NEW_TOP, MASK_DSC } from '../login/AppLoginGql';
import { ApiMsg, AppObject, MenuUrl, MobileMenuId } from '../../value/AppConst';
import UserSessionInfo from '../../value/UserSessionInfo';
import AppHeader, { MobileMenuMap } from './AppHeader';
import TopHome from '../home/TopHome';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import TransPlanStateForDc from '../plan_stat/TransPlanStateForDc';
import TransPlanStateForCompany from '../plan_stat/TransPlanStateForCompany';
import TransPlanTsr from '../tsr/TransPlanTsr';
import TransPlanPos from '../plan_pos/TransPlanPos';
import RtdTermPos from '../rtd_term_pos/RtdTermPos';
import MaskDsc from '../login/MaskDsc';
import { enqueueSnackbar } from 'notistack';
import TransPlanLaunch from '../trans_plan_launch/TransPlanLaunch';
import MapDialog from '../map/MapDialog';
import ArrivalToStore from '../arrival_to_store/ArrivalToStore';
import TransPlanResult from '../trans_plan_result/TransPlanResult';

const AppContainer = styled(Box)({
    display:'flex', flexDirection:'column', height:'100vh',
    position: 'fixed', top:0, left:0, right:0, bottom:0,
    overflow: 'hidden'
});

const AppBody = styled(Box)({flexGrow:1, overflow: 'hidden', display:'flex'}); // flex 해야 사이즈 고정됨.

export default function AppMain({onLogOut}) {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const [mapRequest, setMapRequest] = useState({open:false, mapData: null});
    const mapRequestData = useReactiveVar(mapRequestRepo);
    const navigate = useNavigate();
    const location = useLocation();
    const urlRef = useRef(null);

    useEffect(() => {
        if(mapRequestData && mapRequestData.data) {
            setMapRequest({open:true, mapData: mapRequestData.data});
        }
        else setMapRequest({open:false, mapData: null});
    }, [mapRequestData]);

    const [logoutNewTop, responseLogout] = useLazyQuery(LOGOUT_NEW_TOP, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteLogout(data, option)},
        onError: (error) => alert(error.message),
    });

    const [maskDc, responseMaskDc] = useMutation(MASK_DSC, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteMaskingDc(data, option)},
        onError: (error) => { alert('센터 전환 오류: '+ error.message);},
    });

    const navigateDefaultHome = () => {
        if(sessionInfo.user.hasMasterPower()) navigate(MenuUrl.TopHome);
        else if(sessionInfo.user.isTransCompanyUser()) navigate(MenuUrl.TransPlanStateForCompany);
        else navigate(MenuUrl.TransPlanStateForDc);
    };

    const onCompleteLogout = (data, option) => {
        if(data.logoutNewTop) {
            const sessionInfo = UserSessionInfo.initFromUserObject(data.logoutNewTop);
            userInfoRepo(sessionInfo);
            // Go to root url
            //navigateDefaultHome();
            if(onLogOut) onLogOut();
            enqueueSnackbar('로그아웃 되었습니다.', {variant:'success', autoHideDuration:2000});
        }
        else {
            // logout anyway.
            const sessionInfo = new UserSessionInfo(true); // Session checked true
            userInfoRepo(sessionInfo);
            if(onLogOut) onLogOut();
            enqueueSnackbar('로그아웃 되었습니다...', {variant:'success', autoHideDuration:2000});
            clearReposForNewSession();
        }
    };

    const onCompleteMaskingDc = (data, clientOption) => {
        if(data.maskDsc) {
            if(data.maskDsc.sessionNote === ApiMsg.OK) {
                const session = UserSessionInfo.initFromUserObject(data.maskDsc);
                clearReposForNewSession();
                userInfoRepo(session);
                enqueueSnackbar('센터 전환되었습니다.', {variant:'success', autoHideDuration:2500});
                if(urlRef.current) {
                    navigate(urlRef.current);
                    urlRef.current = null; // Reset the URL reference
                }
                else navigateDefaultHome();
            }
            else {
                alert("센터 전환에 실패했습니다.\n" + data.maskDsc.sessionNote);
            }
        }
        else {
            //addAlert({title:'센터 전환 오류', message:['알 수 없는 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.']});
            alert('센터 전환 중 알 수 없는 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.')
        }
    };

    const onClickLogOut = () => {
        logoutNewTop();
    };

    const onMenuSelected = (menu) => {
        if(menu.menuId===MobileMenuId.LogOut) onClickLogOut();
        else if(MobileMenuMap.hasOwnProperty(menu.menuId)) {
            if(menu.menuId === MobileMenuId.TransPlanState && sessionInfo.user.isTransCompanyUser) {
                navigate(MenuUrl.TransPlanStateForCompany);
            }
            else {
                const url = MobileMenuMap[menu.menuId].url;
                navigate(url);
            }
        }
        else navigateDefaultHome();
    };

    const onRequestChangeDc = () => {
        const currentUrl = location.pathname;
        urlRef.current = currentUrl; // Save current URL
        navigate(MenuUrl.MaskDsc);
    };

    const onRequestAbortMask = () => {
        // go back to the previous URL = as if the user press back button
        navigate(-1);
    };

    const onRequestMask = (dcInfoItem) => {
        const param = dcInfoItem ?
        {variables: {dscId: dcInfoItem.dcCd, dscName: dcInfoItem.dcName}} :
        {variables: {dscId: sessionInfo.user.dscId, dscName: sessionInfo.user.dscName}};
        maskDc(param);
    };

    return(
        <AppContainer>
            <AppHeader onSelectMenu={onMenuSelected} onRequestChangeDc={onRequestChangeDc}/>
            <AppBody>
                <Routes>
                    <Route path="/" element={
                        sessionInfo.user.hasHomplusPower()
                        ?
                        <TopHome />
                        :
                        sessionInfo.user.isTransCompanyUser()
                        ?
                        <TransPlanStateForCompany />
                        :
                        <TransPlanStateForDc />
                    } />
                    <Route path={MenuUrl.TopHome} element={<TopHome />} />
                    <Route path={MenuUrl.TransPlanStateForDc} element={<TransPlanStateForDc />} />
                    <Route path={MenuUrl.TransPlanStateForCompany}
                        element={<TransPlanStateForCompany />}
                    />
                    <Route path={MenuUrl.TransPlanLaunch} element={<TransPlanLaunch />} />
                    <Route path={MenuUrl.TSR} element={<TransPlanTsr />} />
                    <Route path={MenuUrl.Store} element={<ArrivalToStore />} />
                    <Route path={MenuUrl.VehTransPos} element={<TransPlanPos />} />
                    <Route path={MenuUrl.VehLocation} element={<RtdTermPos />} />
                    <Route path={MenuUrl.ManageRentTime} element={<TransPlanResult />} />
                    <Route path={MenuUrl.MaskDsc}
                        element={<MaskDsc onClose={onRequestAbortMask} onRequestMask={onRequestMask} />}
                    />
                </Routes>
            </AppBody>
            <MapDialog
                open={mapRequest.open}
                onClose={() => mapRequestRepo({data: null})}
            />
        </AppContainer>
    );
}

/*

                    <Route path={MenuUrl.TransPlanUpload} element={<TransPlanUpload />} />
                    <Route path={MenuUrl.Report} element={<Reports />} />
                    <Route path={MenuUrl.ManageRent} element={<VehInfoMyDcRented />} />


*/
